import { Component, OnInit } from '@angular/core';
import { ColDef, GridOptions, GridApi } from "ag-grid-community";
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { of, Observable, forkJoin } from "rxjs";
import { tap, switchMap } from 'rxjs/operators';
import { DateService } from '../../../../common/services/date.service';
import { SubHeader } from '../../../../core/models/sub-header.model';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { AdminReportsService } from '../../../services/admin-reports.service';
import { Router } from '@angular/router';
import { SubHeaderService } from '../../../../core/services/sub-header.service';
import { ReportSearchCriteria, WellTransferListingModel } from '../../../models/admin-reports.model';
import { ReportArgs } from "../../../../common/models/reports.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { DomainValue } from '../../../../models/domain.model';
import { DomainDefinitionService } from '../../../../builder/services/domain-definition.service';



@Component({
    selector: 'app-well-transfer-report',
    templateUrl: './well-transfer-report.component.html',
    styleUrls: ['./well-transfer-report.component.scss'],
    standalone: false
})
export class WellTransferReportComponent implements OnInit {

    dateFields: string[] = ["ApprovedDate"];

    dispositions: DomainValue[] = [];
    legacyTrackings: DomainValue[] = [];
    wellTypeDescriptions: DomainValue[] = [];


    get excelStyles(): any[] {
        if (this.formatService) return this.formatService.excelStyles;
        return [];
    }


    gridOptions: GridOptions;
	gridApi?: GridApi;
    currentDate: Date = new Date();   
    includePending: string = "Include Pending / Not Yet Approved Well Transfers";
    toDate: Date | null;
    fromDate: Date | null;

    reportData: WellTransferListingModel[]

    subHeader: SubHeader = new SubHeader({
        title: "Transfer Listing",
        buttons: []
    });

    subscription: Subscription = new Subscription();

    errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", false, () => { return; })]);

    constructor(
        private datePipe: DatePipe,
        private readonly dateService: DateService,
        private readonly formatService: GridFormatterService,
        private readonly faService: AdminReportsService,
        public router: Router,
        public subHeaderService: SubHeaderService,
        public dialogService: DynamicDialogService,
        private domainService: DomainDefinitionService
	) { }

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this.subHeader);
        this.fromDate = this.dateService.generateDateWithOffset(0, -1, 0);
        this.toDate = this.currentDate;


        let coldefs = this.createColumnDefinitions();
        this.gridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(coldefs, false, true, true, true);
		this.gridOptions.headerHeight = 50;
		this.gridOptions.onGridReady = (params) => { 
            this.gridApi = params.api; 
            this.gridApi.sizeColumnsToFit();
        };
        this.generateReport({ fromDate: this.fromDate, toDate: this.toDate, download: false } as ReportArgs);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    generateReport(args: ReportArgs) {
        this.fromDate = args.fromDate;
        this.toDate = args.toDate;

        if (this.gridApi) {            
            this.gridApi.setGridOption("loading", true);
        }

        let disposition$: Observable<DomainValue[]> = (this.dispositions != null && this.dispositions.length > 0)
            ? of(this.dispositions)
            : this.domainService.getValues("STATUS_DESC").pipe(
                tap((data: DomainValue[]) => {                    
                    this.dispositions = data;
                })
            );

        let legacyTracking$: Observable<DomainValue[]> =
            (this.legacyTrackings != null && this.legacyTrackings.length > 0)
                ? of(this.legacyTrackings)
                : this.domainService.getValues("STATUS_REAS").pipe(
                    tap((data: DomainValue[]) => {
                        this.legacyTrackings = data;
                    })
                );
        let wellTypeDescription$: Observable<DomainValue[]> =
            (this.wellTypeDescriptions != null && this.wellTypeDescriptions.length > 0)
                ? of(this.wellTypeDescriptions)
                : this.domainService.getValues("WLTYP_DESC").pipe(
                    tap((data: DomainValue[]) => {
                        this.wellTypeDescriptions = data;
                    })
                );


        const criteria = new ReportSearchCriteria();
        criteria.fromDate = this.fromDate;
        criteria.toDate = this.toDate;
        criteria.dateFields = this.dateFields;

        let obs$ = forkJoin([disposition$, legacyTracking$, wellTypeDescription$]);

        this.subscription = obs$.pipe(switchMap((d: any) => {
                return this.faService.getWellTransferInfo(criteria);
            }),
            tap((data) => {
                this.reportData = data;
            })).subscribe({
                next: (success) => {
                   if (this.gridApi != null) {
                        this.gridApi.setGridOption("rowData", this.reportData);
                        this.gridApi.setGridOption("loading", false);
                        this.gridApi.autoSizeAllColumns();
                   }
                }, 
                error: (fail) => {
                    console.error(fail);
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("loading", false);
                    }
                    this.showError();
                }
            });

    };

    downloadReport(args: ReportArgs) {
        this.fromDate = args.fromDate;
        this.toDate = args.toDate;
        let fileName = "TransferListingReport";
        let sheetName = "Transfer Listing";
        if (this.fromDate != null && this.toDate != null) {
            const frmDt = this.datePipe.transform(this.fromDate, "yyyy-MM-dd").toString();
            const toDt = this.datePipe.transform(this.toDate, "yyyy-MM-dd").toString();
            fileName = `Approved${fileName}_${frmDt.toString()}_${toDt.toString}`;
        } else {
            const now = this.datePipe.transform(new Date(), "yyyy-MM-dd").toString();
            fileName = `Pending${fileName}_${now.toString()}`;
        }

        this.formatService.exportDataToExcel(fileName, sheetName, this.gridApi);
    }



    public createColumnDefinitions(): ColDef[] {
        return [
            {
                headerClass: "wrapped",
                headerName: "API Number",
                headerTooltip: "API Number",
                field: "apiNumber",
                tooltipField: 'apiNumber',
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                cellClass: "stringType",
                minWidth: 150,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Well Name",
                headerTooltip: "Well Name",
                valueGetter: this.formatWellName(),
                tooltipValueGetter: this.formatWellName(),
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 100,
                maxWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Well Number",
                headerTooltip: "Well Number",
                field: "wellNumber",
                tooltipField: 'wellNumber',
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                cellClass: "stringType",
                minWidth: 100,
                maxWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Slant",
                headerTooltip: "Slant",
                field: "slant",
                tooltipField: "slant",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                cellClass: "stringType",
                minWidth: 75,
                maxWidth: 100
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Well Disposition",
                headerTooltip: "Well Disposition",
                field: "disposition",
                tooltipValueGetter: this.formatDomainField("disposition"),
                valueFormatter: this.formatDomainField("disposition"),
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                cellClass: "stringType",
                minWidth: 100,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Legacy Tracking",
                headerTooltip: "Legacy Tracking",
                field: "legacyTracking",
                tooltipValueGetter: this.formatDomainField("legacyTracking"),
                valueFormatter: this.formatDomainField("legacyTracking"),
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                cellClass: "stringType",
                minWidth: 100,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Well Type Description",
                headerTooltip: "Well Type Description",
                field: "wellTypeDescription",
                tooltipValueGetter: this.formatDomainField("wellTypeDescription"),
                valueFormatter: this.formatDomainField("wellTypeDescription"),
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                cellClass: "stringType",
                minWidth: 100,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Transferor",
                headerTooltip: "Transferor",
                field: "transferor",
                tooltipField: 'transferor',
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 150,
                maxWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Transferee",
                headerTooltip: "Transferee",
                field: "transferee",
                tooltipField: 'transferee',
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 150,
                maxWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Sales Date",
                headerTooltip: "Sales Date",
                field: "salesDate",
                tooltipValueGetter: this.formatService.formatDate("salesDate"),
                valueGetter: this.formatService.formatDate("salesDate"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this.formatService.defaultDateFilterParams,
                valueFormatter: this.formatService.formatDate("salesDate"),
                minWidth: 125,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Transfer Approval Date",
                headerTooltip: "Transfer Approval Date",
                field: "transferApprovalDate",
                tooltipValueGetter: this.formatService.formatDate("transferApprovalDate"),
                valueGetter: this.formatService.formatDate("transferApprovalDate"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this.formatService.defaultDateFilterParams,
                valueFormatter: this.formatService.formatDate("transferApprovalDate"),
                minWidth: 125,
                maxWidth: 150
            } as ColDef

        ] as ColDef[];
    }

    get filterDisabled(): string {
        return this.fromDate == null || this.toDate == null ? 'disabled' : null;
    }

    formatDomainField(field: string): any {
        return (obj: any) => {
            let returnValue = "";
            if (obj != null && obj.data != null) {
                switch (field) {
                    case "disposition": {
                        returnValue = obj.data["disposition"];
                        if (returnValue == null) returnValue = "";
                        let match = this.dispositions.find(d => d.Code === returnValue);
                        if (match != null) returnValue = match.Definition;
                        break;
                    }
                    case "legacyTracking": {
                        returnValue = obj.data["legacyTracking"];
                        if (returnValue == null) returnValue = "";
                        let match = this.legacyTrackings.find(d => d.Code === returnValue);
                        if (match != null) returnValue = match.Definition;
                        break;
                    }
                    case "wellTypeDescription": {
                        returnValue = obj.data["wellTypeDescription"];
                        if (returnValue == null) returnValue = "";
                        let match = this.wellTypeDescriptions.find(d => d.Code === returnValue);
                        if (match != null) returnValue = match.Definition;
                        break;
                    }
                       
                }
            }
            return returnValue;
        }
    }

    formatWellName(): any {
        return (obj: any) => {
            let returnValue = "";
            if (obj && obj.data) {
                if (obj.data["leaseName"] != null && obj.data["leaseName"].length > 0) returnValue = obj.data["leaseName"];
                else if (obj.data["wellName"] != null && obj.data["wellName"].length > 0) {
                    returnValue = obj.data["wellName"];
                    if (obj.data["wellNumber"] != null && obj.data["wellNumber"].length > 0) {
                        if (returnValue.trim().endsWith(obj.data["wellNumber"].trim())) {
                            returnValue = returnValue.substring(0, returnValue.length - obj.data["wellNumber"].length);
                        }
                    }
                }
            }
            return returnValue;
        }
    }


    showError(): void {
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }
}